// src/components/contacts/CompanyMultiPicker.tsx
//
// Pick the CANONICAL Companies a contact belongs to, and which one is primary.
//
// Replaces the single-company `CompanyPicker`. A person can be an employee at
// one company, an owner of another and an advisor to a third; the form now
// says so directly instead of forcing one choice and leaving the rest for the
// detail screen.
//
// WHAT IT EMITS — company records and a primary id, never a typed string.
// The create form posts `companyIds` + `primaryCompanyId`; the server links
// them in the same transaction that creates the contact.
//
// PRIMARY — the first company picked becomes primary, because with one
// company there is nothing to choose between. Picking more never moves it.
// Removing the primary while others remain clears it rather than promoting
// another: which organization a person principally belongs to is the user's
// decision, and the form refuses to submit until they make it (the server
// refuses too).
//
// DUPLICATE PREVENTION — select before create, never fuzzy merge. Unchanged
// from `CompanyPicker`: "Create" is offered only when no company in the
// workspace normalizes to the typed name, using the server's own rule, and a
// lost race (409) links the record that now exists instead of erroring.
//
// AUTHORIZATION — listing needs `crm:companies:read`; without it there is no
// picker at all (a text box would recreate the orphan-string defect).
// Creating needs `crm:companies:write` and goes through `POST /api/companies`;
// nothing here mints a Company through a contacts-scoped route.

import { useMemo, useState } from 'react';
import {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useLazyGetCompaniesQuery,
} from '../../api/companiesApi';
import { useCompanyPermissions } from '../../hooks/useContactCompanyPermissions';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { Company } from '../../types/api';
import EntityMultiPicker, { type PickerItem } from './EntityMultiPicker';

/**
 * The server's uniqueness rule, mirrored so "does this already exist?" is
 * asked the same way on both sides. `companies.normalizedName` is
 * `trim().toLowerCase()` and carries a unique index per workspace.
 */
export function normalizeCompanyName(name: string): string {
  return name.trim().toLowerCase();
}

/** The fields the pickers and forms need; a full `Company` satisfies it. */
export type CompanyChoice = Pick<Company, 'id' | 'name'> & { domain?: string | null };

/**
 * Create a Company by name, resolving a lost race to the record that won.
 *
 * Shared by every surface that can create a Company inline, so they agree on
 * what a duplicate is and what happens when one appears mid-flight.
 */
export function useCreateCompanyResolvingDuplicate() {
  const [createCompany, { isLoading }] = useCreateCompanyMutation();
  const [fetchCompanies] = useLazyGetCompaniesQuery();

  async function create(name: string): Promise<CompanyChoice> {
    const typed = name.trim();
    try {
      return await createCompany({ name: typed }).unwrap();
    } catch (err) {
      if ((err as { status?: number })?.status === 409) {
        // Someone created it first. Linking the record that now exists is what
        // the user asked for; a second "Acme" is what they did not.
        const refreshed = await fetchCompanies({ q: typed, limit: 10 }).unwrap();
        const existing = refreshed.companies.find(
          (c) => normalizeCompanyName(c.name) === normalizeCompanyName(typed),
        );
        if (existing) return existing;
        // Present to the server, invisible to this member under record scoping.
        throw new Error('A company with this name already exists but is not visible to you.');
      }
      throw new Error(
        (err as { data?: { error?: string } })?.data?.error || 'Failed to create company',
      );
    }
  }

  return { create, isCreating: isLoading };
}

interface Props {
  value: CompanyChoice[];
  primaryId: string | null;
  onChange: (companies: CompanyChoice[], primaryId: string | null) => void;
  /**
   * Search text typed but NOT resolved to a company. The parent owns submit
   * and refuses it while this is non-empty: a typed name sitting in the box
   * must never be silently dropped (measured on staging), and it must never be
   * saved as an orphan string either.
   */
  onPendingTextChange?: (text: string) => void;
  disabled?: boolean;
  /** Validation message from the parent, e.g. "choose a primary". */
  error?: string | null;
}

const toItem = (c: CompanyChoice): PickerItem => ({ id: c.id, label: c.name, detail: c.domain ?? null });

export default function CompanyMultiPicker({
  value,
  primaryId,
  onChange,
  onPendingTextChange,
  disabled,
  error,
}: Props) {
  const { canViewCompanies, canCreateCompany } = useCompanyPermissions();
  const [query, setQuery] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const debounced = useDebouncedValue(query.trim(), 250);

  const setQueryAndNotify = (next: string) => {
    setQuery(next);
    setCreateError(null);
    onPendingTextChange?.(next);
  };

  const { data, isFetching } = useGetCompaniesQuery(
    { q: debounced || undefined, limit: 10 },
    { skip: !canViewCompanies || !debounced },
  );
  const { create, isCreating } = useCreateCompanyResolvingDuplicate();

  const typed = query.trim();
  const settled = debounced === typed && !isFetching;
  const matches = useMemo(() => (debounced ? (data?.companies ?? []) : []), [data, debounced]);
  const exactMatch = matches.some((c) => normalizeCompanyName(c.name) === normalizeCompanyName(typed));
  const alreadySelected = value.some((c) => normalizeCompanyName(c.name) === normalizeCompanyName(typed));

  if (!canViewCompanies) return null;

  function add(company: CompanyChoice) {
    if (value.some((c) => c.id === company.id)) return;
    const next = [...value, company];
    // First company is primary; later ones never move it.
    onChange(next, primaryId ?? (value.length === 0 ? company.id : null));
  }

  function remove(id: string) {
    const next = value.filter((c) => c.id !== id);
    // Removing the primary clears it unless only one company is left, which
    // is then the only possible answer.
    let nextPrimary = primaryId === id ? null : primaryId;
    if (nextPrimary === null && next.length === 1) nextPrimary = next[0].id;
    onChange(next, nextPrimary);
  }

  async function handleCreate() {
    setCreateError(null);
    try {
      const company = await create(typed);
      add(company);
      setQueryAndNotify('');
    } catch (err) {
      setCreateError((err as Error).message);
    }
  }

  const companiesById = new Map<string, CompanyChoice>(matches.map((c) => [c.id, c]));

  const hint =
    typed.length > 0
      ? canCreateCompany
        ? 'Pick a company from the list, or create it — typed text on its own is not saved.'
        : 'Pick a company from the list — typed text on its own is not saved.'
      : value.length > 1 && !primaryId
        ? 'Choose which company is primary.'
        : null;

  return (
    <EntityMultiPicker
      testId="company-multi-picker"
      label="Companies"
      placeholder="Search companies..."
      query={query}
      onQueryChange={setQueryAndNotify}
      options={matches.map(toItem)}
      isFetching={!settled}
      selected={value.map(toItem)}
      onAdd={(item) => {
        const company = companiesById.get(item.id);
        if (company) add(company);
      }}
      onRemove={remove}
      renderSelectedExtra={(item) =>
        primaryId === item.id ? (
          <span
            className="shrink-0 text-[10px] uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5"
            aria-label={`${item.label} is the primary company`}
          >
            Primary
          </span>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(value, item.id)}
            aria-label={`Make ${item.label} the primary company`}
            className="shrink-0 text-xs text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 disabled:opacity-50"
          >
            Make primary
          </button>
        )
      }
      createAction={
        canCreateCompany && typed && settled && !exactMatch && !alreadySelected
          ? { label: `+ Create company “${typed}”`, onCreate: handleCreate, isCreating }
          : null
      }
      emptyText="No matching companies."
      hint={hint}
      error={createError ?? error ?? null}
      disabled={disabled}
    />
  );
}
